import * as React from 'react';
import PropTypes from 'prop-types';
import Box from '@mui/material/Box';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TablePagination from '@mui/material/TablePagination';
import TableRow from '@mui/material/TableRow';
import TableSortLabel from '@mui/material/TableSortLabel';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import Paper from '@mui/material/Paper';
import Tooltip from '@mui/material/Tooltip';
import { visuallyHidden } from '@mui/utils';
import { Button, Dialog, DialogActions, DialogContent, Divider, Grid, LinearProgress, Switch, TextField } from '@mui/material';
import { useDispatch, useSelector } from 'react-redux';
import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import { ROWS_PER_PAGE } from '../../utils/constant';
import { encrypt, formatDate } from '../../utils/helper';
import Confirmation from '../Common/Confirmation';
import { toggleActivationInstagramAC,   createInstagramAC, deleteInstagramAC, fetchinstagramAC } from '../../store/cookieSlice';
import CookieForm from '../Cookie/CookieForm';
import { NoData } from '../Common/NoData';
import FilterListOffIcon from '@mui/icons-material/FilterListOff';
import RefreshIcon from '@mui/icons-material/Refresh';
import * as Yup from "yup";
import { useFormik } from 'formik';
import PasswordVisibility from '../Common/PasswordVisibility';
import { toast } from 'react-toastify';
import InstagramForm from '../instagram/InstagramForm' 
import { useTheme } from '@mui/material/styles';
import { alpha } from '@mui/material/styles';

function createData(id, email, expireAt, status, active, actions) {
    return {
        id,
        email,
        expireAt,
        status,
        active,
        actions
    };
}

const InstagramActions = ({ page, setPage, getCookies, cookie, setSelecteCookie, setCookieModal, canDelete, canEdit, canAdd }) => {
    const [isOpen, setIsOpen] = React.useState(false);
    const [open, setOpen] = React.useState(false);
    const [openRegenerate, setOpenRegenerate] = React.useState(false);
    const dispatch = useDispatch()
    const handleDelete = async (id) => {
        try {
            dispatch(deleteInstagramAC(id)).then(() => {
                getCookies()
            })
        } catch (error) {
            console.error(error);
        }
    }
    const validationSchema = Yup.object({
        password: Yup.string()
            .required("Password is required")
    });
    const [showPassword, setShowPassword] = React.useState({
        password: false,
    });

    const togglePasswordVisibility = (field) => {
        setShowPassword((prevState) => ({
            ...prevState,
            [field]: !prevState[field],
        }));
    };

    const formik = useFormik({
        initialValues: {
            password: "",
        },
        validationSchema: validationSchema,
        onSubmit: async (values) => {
            try {
                setOpenRegenerate(false);
                const toastId = toast.loading("We're processing the account credentials. This may take a few moments");
                try {
                    const data = { password:encrypt(values.password), integration: "instagram", email: cookie.email };
                    dispatch(createInstagramAC(data)).then((res) => {
                        if (page) {
                            setPage(0)
                        } 
                        getCookies()
                        toast.update(toastId, {
                            render: "Will take some time to complete the process",
                            type: "warning",
                            isLoading: false,
                            autoClose: 3000,
                        });
                    }).catch(error => {
                        toast.update(toastId, {
                            render: "Something went wrong. Please try again.",
                            type: "error",
                            isLoading: false,
                            autoClose: 3000,
                        });
                    })
                } catch (error) {
                    toast.update(toastId, {
                        render: "Something went wrong. Please try again.",
                        type: "error",
                        isLoading: false,
                        autoClose: 3000,
                    });
                }
            } catch (error) {
                console.error(error);
            }
        }
       
    });
    return (
        <>
            <Grid spacing={4} justifyContent={'flex-end'}>
                <Grid size={8}>
                    <Box gap={2} display='flex' className="table-margin">
                        <Tooltip title="Login Again">
                            <RefreshIcon className={`${!canAdd && 'icon-disabled'}`} color='primary' onClick={() => {
                                if (canAdd) {
                                    setOpenRegenerate(true)
                                }
                            }} />
                        </Tooltip>
                        <DeleteOutlineOutlinedIcon className={`${!canDelete && 'icon-disabled'} danger-color`} onClick={() => {
                            if (canDelete) {
                                setIsOpen(true)
                            }

                        }} />

                    </Box>
                    <>
                        <Dialog
                            open={openRegenerate}
                            onClose={() => setOpenRegenerate(false)}
                            aria-labelledby="alert-dialog-title"
                            aria-describedby="alert-dialog-description"
                            fullWidth
                            maxWidth="md"
                            PaperProps={{
                                sx: {
                                    width: '600px', 
                                    maxWidth: '90%', 
                                    padding: 2
                                }
                            }}
                        >
                            <DialogContent>
                                <Box
                                    component="form"
                                    onSubmit={formik.handleSubmit}
                                >
                                    <Typography variant="h6" gutterBottom>
                                        Login Again
                                    </Typography>
                                    <Divider />

                                    <TextField
                                        required
                                        fullWidth
                                        id="password"
                                        name="password"
                                        label="Password"
                                        type={showPassword.password ? "text" : "password"}
                                        margin="normal"
                                        variant="outlined"
                                        value={formik.values.password}
                                        onChange={formik.handleChange}
                                        onBlur={formik.handleBlur}
                                        error={formik.touched.password && Boolean(formik.errors.password)}
                                        helperText={formik.touched.password && formik.errors.password}
                                        InputProps={{
                                            endAdornment: (
                                                <PasswordVisibility
                                                    showPassword={showPassword.password}
                                                    onToggle={() => togglePasswordVisibility("password")}
                                                />
                                            ),
                                        }}
                                    />
                                    <Box
                                        display="flex"
                                        justifyContent="flex-end"
                                        gap={1}
                                        mt={1}
                                    >
                                        <Button onClick={() => { setOpenRegenerate(false) }} className="mr-2" variant="outlined">Cancel</Button>
                                        <Button type="submit" variant="contained" disabled={!formik.isValid}>
                                            Process
                                        </Button>
                                    </Box>
                                </Box>
                            </DialogContent>
                        </Dialog>
                        <Confirmation open={isOpen} setOpen={setIsOpen} msg={"Are you sure you want to remove this Account"} yesClick={() => { handleDelete(cookie.uuid); setIsOpen(false) }} />
                    </>
                </Grid>
            </Grid>
        </>
    )
}


const headCells = [
    {
        label: 'Email',
        numeric: false,
        disablePadding: false,
        id: 'email',
        sortable: true
    },
    {
        label: 'Expired At',
        numeric: false,
        disablePadding: false,
        id: 'expire_at',
        sortable: true
    },
    {
        label: 'Status',
        numeric: false,
        disablePadding: false,
        id: 'status',
        sortable: true
    },
    {
        label: 'Active',
        numeric: false,
        disablePadding: false,
        id: 'active',
        sortable: false
    },
    {
        label: 'Actions',
        numeric: false,
        disablePadding: false,
        id: 'actions',
        sortable: false
    },
];

function EnhancedTableHead(props) {
    const { setOrdering, orderingState, setOrderingState } =
        props;

    const createSortHandler = (property) => (event) => {
        const orderData = orderingState
        const exists = orderData?.find((item) => item.id === property);
        const updated = (() => {

            if (exists) {
                return orderData.map((item) =>
                    item.id === property
                        ? { ...item, order: item.order === "asc" ? "desc" : "asc" }
                        : item
                );
            } else {
                return [...orderData, { id: property, order: "asc" }];
            }
        })();
        setOrderingState(updated)

    };


    React.useEffect(() => {
        const ordering = orderingState.map((item) => {
            return item.order === "asc" ? item.id : "-" + item.id;
        });
        if (ordering?.length) {
            setOrdering(ordering.join(","));
        }
        console.log("orderingState", orderingState);

    }, [orderingState]);

    const removeFilter = (property) => {
        const data = orderingState.filter((item) => item.id !== property)
        setOrderingState(data)
    }
    return (
        <TableHead>
            <TableRow>
                
                {headCells.map((headCell) => {
                    const key = headCell.id;
                    const sortObj = orderingState.find((item) => item.id === key);
                    const isSorted = !!sortObj;
                    const sortDirection = sortObj?.order || "asc";

                    return (<TableCell
                        className="font-weight"
                        key={key}
                        align={headCell.numeric ? "left" : "left"}
                        padding={headCell.disablePadding ? "none" : "normal"}
                        sortDirection={isSorted ? sortDirection : false}
                    >
                        {headCell.sortable ? <TableSortLabel
                            active={isSorted}
                            direction={sortDirection}
                            onClick={createSortHandler(key)}
                            color="secondary"
                            hideSortIcon={false}
                        >
                            {headCell.label}
                            {isSorted && (
                                <Box component="span" sx={visuallyHidden}>
                                    {sortDirection === "desc" ? "sorted descending" : "sorted ascending"}
                                </Box>
                            )}
                            {isSorted && <FilterListOffIcon onClick={(e) => { e.stopPropagation(); removeFilter(headCell.id) }} className="ml-2" size="small" color='primary' />}
                        </TableSortLabel> : headCell.label}
                    </TableCell>)
                })}
            </TableRow>
        </TableHead>
    );
}

EnhancedTableHead.propTypes = {
    numSelected: PropTypes.number.isRequired,
    onRequestSort: PropTypes.func.isRequired,
    onSelectAllClick: PropTypes.func.isRequired,
    order: PropTypes.oneOf(['asc', 'desc']).isRequired,
    orderBy: PropTypes.string.isRequired,
    rowCount: PropTypes.number.isRequired,
};
function EnhancedTableToolbar(props) {
    const { numSelected } = props;
    const { cookieModal, page, setPage, getCookies, setCookieModal, selecteCookie, setSelecteCookie, setResetTrigger, resetTrigger } = props;
    return (
        <>
            <InstagramForm {...{ page, setPage, getCookies, cookieModal, setCookieModal, setSelecteCookie, selecteCookie, resetTrigger }} title={selecteCookie ? "Edit" : "Create"} />
            <Toolbar
                className='main-list-toolbar'
            >
                <Typography
                    className="d-flex flex-grow-1 flex-shrink-1 flex-fill"
                    variant="h6"
                    id="tableTitle"
                    component="div"
                >
                    Accounts
                </Typography>

                <Tooltip title="Create">
                    <Button variant="contained" onClick={() => {
                        setResetTrigger(!resetTrigger)
                        setSelecteCookie(null);
                        setCookieModal(true);

                    }}>Add Account</Button>
                </Tooltip>

            </Toolbar>
        </>
    );
}

EnhancedTableToolbar.propTypes = {
    numSelected: PropTypes.number.isRequired,
};

export default function InstagramManage() {
    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('calories');
    const [selected, setSelected] = React.useState([]);
    const [rows, setRows] = React.useState([]);
    const [page, setPage] = React.useState(0);
    const [rowsPerPage, setRowsPerPage] = React.useState(ROWS_PER_PAGE);
    const [cookieModal, setCookieModal] = React.useState(false);
    const [selecteCookie, setSelecteCookie] = React.useState({});
    const [resetTrigger, setResetTrigger] = React.useState(false);
    const [queryString, setQueryString] = React.useState("");
    const [opneActiveConfirm, setOpneActiveConfirm] = React.useState(false);
    const cookies = useSelector((state) => state.cookieReducer.instagramAC)
    const loading = useSelector((state) => state.cookieReducer.loading)
    const cookieCount = useSelector((state) => state.cookieReducer.count)
    const userPermission = useSelector((state) => state.userReducer.userPermission)
    const cookiePermissions = userPermission.find((perm) => perm.model_name === "platform")?.permissions || [];
    let canEdit = cookiePermissions.includes("change");
    let canDelete = cookiePermissions.includes("delete");
    let canAdd = cookiePermissions.includes("add");
    const [orderingState, setOrderingState] = React.useState([])
    const [ordering, setOrdering] = React.useState('-updated_at');
    const dispatch = useDispatch()
    const theme = useTheme();

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const handleSelectAllClick = (event) => {
        if (event.target.checked) {
            const newSelected = rows.map((n) => n.id);
            setSelected(newSelected);
            return;
        }
        setSelected([]);
    };

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const label = { inputProps: { 'aria-label': 'Color switch demo' } };

    React.useEffect(() => {
        setQueryString(`?page=${page + 1}&page_size=${rowsPerPage}&ordering=${ordering}`)
    }, [ordering, page, rowsPerPage])

    const getCookies = () => {
        dispatch(fetchinstagramAC(queryString))
    }
    React.useEffect(() => {
        if(queryString){
            getCookies()
        }
    }, [queryString])

    React.useEffect(() => {
        const rowData = cookies?.map((ele) => {
            return createData(
                ele.id,
                ele.email,
                ele?.expire_at,
                ele?.status,
                <Switch disabled={loading} className={`${!canEdit && 'icon-disabled'}`} checked={ele.deactivated_on ? false : true} {...label} onClick={(e) => {
                    if (canEdit) {
                        setSelecteCookie(ele)
                        if (!ele?.deactivated_on) {
                            setOpneActiveConfirm(true)
                        } else {
                            dispatch(toggleActivationInstagramAC(ele.uuid))
                        }
                    }
                }} />,
                <InstagramActions {...{ page, setPage, getCookies, cookieModal, canAdd, setCookieModal, setSelecteCookie, canDelete, canEdit }} cookie={ele} />
            )
        })
        setRows(rowData)
    }, [cookies, loading])

    function Row(props) {
        const { row, labelId } = props;

        // Helper to get color values with fallback
        const getColor = (key, fallback) =>
            theme.palette.custom?.[key] || fallback;

        // Determine status key and label
        const statusKey = row?.status;
        const label = statusKey || "Unknown";

        // Compute styles
        const buttonStyles = {
            color: getColor(statusKey, theme.palette.text.primary),
            backgroundColor: alpha(
                getColor(statusKey + "Background", theme.palette.background.paper),
                0.1
            ),
            borderColor: getColor(statusKey + "Border", theme.palette.divider),
            textTransform: "capitalize",
            "&:hover": {
                backgroundColor: alpha(
                    getColor(statusKey + "Background", theme.palette.background.paper),
                    0.2
                ),
                borderColor: getColor(statusKey + "Border", theme.palette.divider),
            },
        };

        return (
            <React.Fragment>
                <TableRow
                    hover
                    role="checkbox"
                    tabIndex={-1}
                    key={row.id}
                    id={labelId}
                    className="cursor-pointer"
                >
                    <TableCell align="left">{row.email}</TableCell>
                    <TableCell align="left">{formatDate(row.expireAt) ||"N/A"}</TableCell>
                    <TableCell align="left">
                        <Button
                            size="small"
                            sx={buttonStyles}
                            variant="outlined"
                        >
                            {label}
                        </Button>
                    </TableCell>
                    <TableCell align="left">{row.active}</TableCell>
                    <TableCell align="left">{row.actions}</TableCell>
                </TableRow>
            </React.Fragment>
        );
    }

    return (
        <Box className='w-100'>
            <Confirmation open={opneActiveConfirm} setOpen={setOpneActiveConfirm} msg={"Are you sure you want to inactive this account"} yesClick={() => { dispatch(toggleActivationInstagramAC(selecteCookie.uuid)); setOpneActiveConfirm(false) }} />
            <Paper className="w-100 mb-2" >
                <EnhancedTableToolbar numSelected={selected.length} {...{ page, setPage, getCookies, cookieModal, setCookieModal, setSelecteCookie, selecteCookie, setResetTrigger, resetTrigger }} />
                {loading && <LinearProgress />}
                <TableContainer className='position-relative'>
                    <Table
                        className='min-width-750'
                        aria-labelledby="tableTitle"
                        size={'small'}
                    >
                        <EnhancedTableHead
                            numSelected={selected.length}
                            orderingState={orderingState}
                            setOrderingState={setOrderingState}
                            setOrdering={setOrdering}
                            onSelectAllClick={handleSelectAllClick}
                            onRequestSort={handleRequestSort}
                            rowCount={rows?.length}
                        />
                        <TableBody>
                            {rows?.map((row, index) => {
                                const labelId = `enhanced-table-checkbox-${index}`;
                                return (
                                    <>
                                        <Row row={row} label={labelId} />
                                    </>
                                );
                            })}
                        </TableBody>
                    </Table>
                    {!cookies?.length && !loading && <NoData></NoData>}
                </TableContainer>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={cookieCount}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
        </Box>
    );
}
